import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { markNotificationRead } from '../services/notificationService';

/**
 * Subscribes to Supabase Realtime for new incentive_notifications
 * addressed to the current user.
 *
 * - On INSERT: queues the notification and shows it as an immediate popup.
 * - Popup stays visible until user dismisses or takes action.
 * - If dismissed, a persistent badge flag is set.
 * - Badge clears when user takes action.
 */
const useRealtimeNotifications = (userId) => {
  const [queue, setQueue] = useState([]);
  const [currentPopup, setCurrentPopup] = useState(null);
  const [hasPersistentBadge, setHasPersistentBadge] = useState(false);
  const channelRef = useRef(null);
  const badgeKey = 'rt_notification_badge';

  // Restore badge from sessionStorage on mount
  useEffect(() => {
    const stored = sessionStorage.getItem(badgeKey);
    if (stored === 'true') setHasPersistentBadge(true);
  }, []);

  // Advance queue: show next popup when current is cleared
  useEffect(() => {
    if (!currentPopup && queue?.length > 0) {
      const next = queue[0];
      const rest = queue.slice(1);
      setCurrentPopup(next);
      setQueue(rest);
    }
  }, [currentPopup, queue]);

  // Subscribe to Supabase Realtime
  useEffect(() => {
    if (!userId) return;

    // Clean up any existing channel
    if (channelRef?.current) {
      supabase?.removeChannel(channelRef?.current);
      channelRef.current = null;
    }

    const channel = supabase
      ?.channel(`realtime-notifications-${userId}`)
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'incentive_notifications',
          filter: `recipient_user_id=eq.${userId}`,
        },
        (payload) => {
          const newNotification = payload?.new;
          if (!newNotification) return;
          // Add to queue — will be shown as popup
          setQueue(prev => [...prev, newNotification]);
          // Set persistent badge immediately
          setHasPersistentBadge(true);
          sessionStorage.setItem(badgeKey, 'true');
        }
      )
      ?.subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log('[RealtimeNotifications] Subscribed for user:', userId);
        }
        if (status === 'CHANNEL_ERROR') {
          console.warn('[RealtimeNotifications] Channel error for user:', userId);
        }
      });

    channelRef.current = channel;

    return () => {
      if (channelRef?.current) {
        supabase?.removeChannel(channelRef?.current);
        channelRef.current = null;
      }
    };
  }, [userId]);

  // User clicks action button — navigate to ticket, clear badge
  const handleAction = useCallback(async () => {
    if (currentPopup?.id) {
      await markNotificationRead(currentPopup?.id)?.catch(() => {});
    }
    setCurrentPopup(null);
    setHasPersistentBadge(false);
    sessionStorage.removeItem(badgeKey);
  }, [currentPopup]);

  // User dismisses popup — badge persists
  const handleDismiss = useCallback(() => {
    setCurrentPopup(null);
    // Badge already set when notification arrived
  }, []);

  // Called externally when all notifications are cleared
  const clearBadge = useCallback(() => {
    setHasPersistentBadge(false);
    sessionStorage.removeItem(badgeKey);
  }, []);

  return {
    currentPopup,
    hasPersistentBadge,
    handleAction,
    handleDismiss,
    clearBadge,
  };
};

export default useRealtimeNotifications;
